import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "@phosphor-icons/react";

const KEY = "exodus_profile_v1";

/**
 * Stores lightweight visitor profile (name + last quiz match) when known.
 * On return, shows a friendly banner with a context-aware CTA.
 */
export function useVisitorProfile() {
  const get = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
  };
  const set = (p) => localStorage.setItem(KEY, JSON.stringify(p));
  const clear = () => localStorage.removeItem(KEY);
  return { get, set, clear };
}

export default function VisitorBanner() {
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(KEY) || "null");
      setProfile(p);
    } catch { /* noop */ }
  }, []);

  if (!profile || dismissed) return null;
  // Don't show on advisor pages, login, or quiz itself
  if (loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/login") || loc.pathname.startsWith("/quiz")) return null;

  const firstName = (profile.name || "").split(" ")[0] || "friend";
  const fit = profile.primary_label;

  // Context-aware CTA based on last known state
  let cta = { to: "/learn", label: "Continue your library tour" };
  if (fit) cta = { to: "/reserve", label: "Book an Illustration Review (Leon)" };

  return (
    <div
      data-testid="visitor-banner"
      className="fixed bottom-6 left-6 right-6 lg:left-auto lg:right-6 lg:w-96 z-40 bg-exodus-surface border border-exodus-gold p-5 shadow-2xl"
    >
      <button
        data-testid="visitor-banner-close"
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 text-exodus-muted hover:text-exodus-text"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
      <p className="eyebrow gold-text">Welcome back</p>
      <p className="font-display text-2xl mt-2 leading-tight">
        Good to see you, {firstName}.
      </p>
      <p className="text-sm text-exodus-muted mt-2">
        {fit ? <>Your match is <span className="text-exodus-text">{fit}</span>.</> : "Pick up where you left off."}
      </p>
      <Link
        to={cta.to}
        data-testid="visitor-banner-cta"
        onClick={() => setDismissed(true)}
        className="inline-flex items-center gap-2 mt-4 text-sm gold-text hover:underline"
      >
        {cta.label} <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}
